/**
 * The glossary — plain-English explainers for the nautical shorthand that peppers
 * a chartplotter. The whole idea is that a beginner shouldn't have to already
 * know what CPA or XTD means to use the app. Tap the term where it sits on the
 * screen and a one-liner explains it, in the same teaching voice we'd use stood
 * next to someone on the boat. If a student doesn't follow a label, that's on us
 * to explain, not on them to have looked it up.
 *
 * Each entry carries the term spelled out in full (the popover heading), a short
 * one-line definition (what actually shows), and an optional longer `full` for a
 * future "tell me more" tap. The definitions are deliberately concrete and always
 * carry the WHY, not just the bare fact.
 *
 * Adding a term later is a one-liner: drop another entry in the map below and it
 * can be wired up wherever it appears with <GlossaryTerm termKey="...">. The keys
 * are lower-case, no spaces, so they're stable to reference from the UI.
 */

export interface GlossaryEntry {
  /** The term written out in full — shown as the popover's heading. */
  term: string;
  /** The one-line, plain-English definition. This is what shows on a tap. */
  short: string;
  /** Optional longer explanation, for a future "more" affordance. */
  full?: string;
}

/**
 * The keyed glossary. `satisfies` keeps every entry type-checked while still
 * giving us a precise union of the real keys (GlossaryKey below), so a typo in a
 * wired term is a compile error rather than a silent miss.
 */
export const GLOSSARY = {
  sog: {
    term: 'Speed Over Ground',
    short:
      "How fast you're actually moving across the seabed, tide and all. It's what the GPS sees, and it's what really matters for working out when you'll arrive.",
  },
  cog: {
    term: 'Course Over Ground',
    short:
      "The direction you're actually travelling across the ground, once the tide and leeway have had their say. It can differ from where the bow is pointing.",
  },
  stw: {
    term: 'Speed Through the Water',
    short:
      'How fast you\'re moving through the water itself, what the log reads. Add the tide to this and you get your speed over the ground.',
  },
  hdg: {
    term: 'Heading',
    short:
      "The direction the bow is pointing. Not necessarily the way you're going, because tide and wind push you off it.",
  },
  xtd: {
    term: 'Cross-Track Distance',
    short:
      "How far off to one side of your planned route line you actually are. Zero means you're bang on the line, and the app tells you which way to steer to get back.",
  },
  cpa: {
    term: 'Closest Point of Approach',
    short:
      'The nearest another vessel will get to you if you both hold your course and speed. The smaller it is, the more you need to be paying attention.',
  },
  tcpa: {
    term: 'Time to CPA',
    short:
      "How long until you and another vessel are at your closest. A small CPA that's still an hour away is less pressing than one that's two minutes off.",
  },
  leeway: {
    term: 'Leeway',
    short:
      'The sideways push the wind gives you through the water, so you end up a little downwind of where you\'re pointing. More boat above the water and stronger wind means more of it.',
  },
  setdrift: {
    term: 'Set and Drift',
    short:
      'The tide is a moving river of water. Set is the direction it\'s flowing, drift is how fast. Together they shift you off your course whether you like it or not.',
  },
  tidalstream: {
    term: 'Tidal Stream',
    short:
      "The horizontal flow of the tide, the water actually moving past you. It can carry you along nicely or push you back, so it's worth knowing which way it's going and how hard.",
  },
  ep: {
    term: 'Estimated Position',
    short:
      'Your best guess of where you are, from your last fix plus the course you\'ve steered, the tide, and the leeway. Better than dead reckoning because it accounts for the water moving you about.',
  },
  dr: {
    term: 'Dead Reckoning',
    short:
      'Working out roughly where you are from just your course and speed since the last fix, before adding tide or leeway. A rough position to build on.',
  },
  fix: {
    term: 'Fix',
    short:
      'A properly worked-out position, from GPS or from crossing a couple of bearings. The thing you build all your other estimates from.',
  },
  mob: {
    term: 'Man Overboard',
    short:
      'Marks the spot the instant someone goes in and starts working the drift so you can get back to them. Every second counts, so it\'s one big button.',
  },
  datum: {
    term: 'Chart Datum',
    short:
      "The reference everything's measured from. For depths it's roughly the lowest the tide normally falls, so there's almost always at least the charted depth of water under you.",
  },
  safetydepth: {
    term: 'Safety Depth',
    short:
      'The least water you\'re happy to float in, your draught plus a margin. The app warns you before you\'d cross into anything shallower.',
  },
  safetycontour: {
    term: 'Safety Contour',
    short:
      "The depth line you've told the app is your no-go boundary. Inside it the water's too shallow for you, so it's shaded and alarmed.",
  },
  drying: {
    term: 'Drying Height',
    short:
      'Ground that\'s covered at high water but dries out at low, the green areas. The figure is how far it stands above chart datum.',
  },
  hat: {
    term: 'Highest Astronomical Tide',
    short:
      'The highest the tide will ever reach under normal conditions, no surge. Handy for bridge and cable clearances, where you want the worst case.',
  },
  scope: {
    term: 'Scope',
    short:
      'How much anchor cable you let out compared to the depth. More scope means the pull on the anchor is flatter along the seabed, so it digs in and holds better.',
  },
  springsneaps: {
    term: 'Springs and Neaps',
    short:
      'The tide breathes on a fortnightly cycle. Springs are the big tides (high highs, low lows, strong streams) around new and full moon; neaps are the gentle ones in between.',
  },
  variation: {
    term: 'Variation',
    short:
      "The difference between true north (the top of the chart) and magnetic north (where the compass points), because the two aren't in the same place. It shifts depending on where you are.",
  },
  transit: {
    term: 'Transit',
    short:
      "Two fixed objects lined up one behind the other. While they stay in line you're on a dead-straight track, no maths needed, a lovely way to check you're not being set off.",
  },
  clearingbearing: {
    term: 'Clearing Bearing',
    short:
      'A bearing you make sure not to cross, that keeps you safely clear of a hazard. Stay the right side of it and you know you\'re in clear water.',
  },
  tidalgate: {
    term: 'Tidal Gate',
    short:
      "A stretch you can only get through around a certain state of tide, either for depth or because the stream's too strong against you. Time it right and it's easy, time it wrong and you wait.",
  },
} satisfies Record<string, GlossaryEntry>;

/** Every valid glossary key — a precise union derived from the map above. */
export type GlossaryKey = keyof typeof GLOSSARY;

/**
 * Look up a term. Takes any string so callers can pass a dynamic label without a
 * cast; returns undefined for anything not in the glossary, so an unknown key
 * just renders as plain text rather than throwing.
 */
export function getGlossaryEntry(key: string): GlossaryEntry | undefined {
  return (GLOSSARY as Record<string, GlossaryEntry>)[key];
}

/** Whether a key resolves to a real entry. Handy for conditional wiring. */
export function hasGlossaryEntry(key: string): key is GlossaryKey {
  return Object.prototype.hasOwnProperty.call(GLOSSARY, key);
}
